import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { LogOut, Info } from 'lucide-react';
import { familyPlanService } from '../../services/familyPlanService';

interface FamilyPlanMemberStatusProps {
  ownerEmail: string | null;
  subscriptionEndDate: string | null;
  onLeave: () => void;
}

const FamilyPlanMemberStatus: React.FC<FamilyPlanMemberStatusProps> = ({
  ownerEmail,
  subscriptionEndDate,
  onLeave
}) => {
  const [isLeaving, setIsLeaving] = useState(false);
  const [error, setError] = useState('');

  const handleLeave = async () => {
    if (!window.confirm('Are you sure you want to leave this family plan? You will lose access to Pro-level features.')) {
      return;
    }

    setIsLeaving(true);
    setError('');

    try {
      await familyPlanService.leaveFamilyPlan();
      onLeave();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to leave family plan');
    } finally {
      setIsLeaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Family Plan Member</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            You are a member of {ownerEmail ? `${ownerEmail}'s` : 'a'} family plan.
            You have access to Pro-level features as part of this plan.
          </AlertDescription>
        </Alert>

        {subscriptionEndDate && (
          <div className="text-sm">
            <p className="text-muted-foreground">Subscription active until:</p>
            <p className="font-medium">{new Date(subscriptionEndDate).toLocaleDateString()}</p>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button
          variant="destructive"
          onClick={handleLeave}
          disabled={isLeaving}
          className="w-full"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {isLeaving ? 'Leaving...' : 'Leave Family Plan'}
        </Button>

        <p className="text-xs text-muted-foreground">
          If you leave, you will revert to the free tier. You can be added to another family plan
          or subscribe to your own plan at any time.
        </p>
      </CardContent>
    </Card>
  );
};

export default FamilyPlanMemberStatus;
